import { useState } from 'react';
import { AxiosError } from 'axios';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { HttpStatusCodeEnum, useUploadFile } from '@/api';
import { ALLOWED_EXTENSIONS, MAX_SIZE_MB } from '@/shared/hooks';
import { alertDialog } from '@/ui_v3';

import { type LearningMaterial } from './LearningMaterialUploadModal';

export const useUploadedFile = (
  file: Pick<LearningMaterial, 'url' | 'originalFileName' | 'fileSize'> | null,
) => {
  const { t } = useTranslation(['common']);
  const [uploadedFile, setUploadedFile] = useState(file);

  const { mutateAsync, isPending } = useUploadFile({
    request: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  });

  const uploadFile = async (file: File) => {
    try {
      const {
        data: { fileUrl },
      } = await mutateAsync({
        data: {
          file,
        },
      });

      setUploadedFile({
        url: fileUrl,
        originalFileName: file.name,
        fileSize: file.size,
      });
    } catch (error) {
      let title = '';
      let description = '';
      let errorStatus: HttpStatusCodeEnum | null = null;

      if (error instanceof AxiosError) {
        switch (error.response?.status) {
          case undefined:
            break;
          case HttpStatusCodeEnum.PAYLOAD_TOO_LARGE_413: {
            title = t('common:common.validateFile.confirmFileSize');
            description = t('common:common.validateFile.possibleMaxFileSize', {
              fileSize: MAX_SIZE_MB.learningMaterial + 'MB',
            });
            errorStatus = HttpStatusCodeEnum.PAYLOAD_TOO_LARGE_413;
            break;
          }
          case HttpStatusCodeEnum.UNSUPPORTED_MEDIA_TYPE_415: {
            title = t('common:common.validateFile.confirmFileExt');
            description = t('common:common.validateFile.possibleFileExt', {
              fileExt: ALLOWED_EXTENSIONS.learningMaterial.join(', '),
            });
            errorStatus = HttpStatusCodeEnum.UNSUPPORTED_MEDIA_TYPE_415;
            break;
          }
        }

        logger.track('upload_learningMaterial_error', {
          type: errorStatus ?? error.response?.status,
          isClientError: false,
        });

        if (title) {
          alertDialog.open({
            header: {
              title,
            },
            body: description,
          });
        }
      }
    }
  };

  return { uploadedFile, uploadFile, isUploading: isPending };
};
